import { useQuery } from '@tanstack/react-query'
import { getCookie } from 'cookies-next'
import { useCallback } from 'react'

import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'

import { getUserQuery } from '../../common'
import { AuthService } from '..'
import { SignByCodeInArgs } from '../lib/auth.types'
import { useAuthCodeStore } from './useAuthCodeStore'

export const useAuth = () => {
  const {
    data: user,
    isLoading: userLoading,
    refetch: refetchUser,
    isSuccess
  } = useQuery({
    ...getUserQuery(),
    enabled: !!getCookie(AuthService.TOKENS_COOKIES_KEY)
  })

  const { setCountdownDate, setErrorStatus } = useAuthCodeStore()
  const { reachGoal } = useYandexMetrika()

  const sendSmsCode = useCallback(
    (methodData: { email?: string; phone_number?: string }) =>
      AuthService.sendSmsCode(methodData).then(response => {
        setCountdownDate(response.headers?.['retry-after'] || 60)
        setErrorStatus(response.errors?.status)
        return response
      }),
    [setCountdownDate, setErrorStatus]
  )

  const loginByCode = useCallback(
    ({ email, phone_number, smsCode }: SignByCodeInArgs) => {
      return AuthService.signInByCode({
        smsCode,
        ...(email && { email }),
        ...(phone_number && { phone_number })
      }).then(response => {
        if (response.ok) {
          refetchUser()
        } else {
          setErrorStatus(response.errors?.status)
        }
        return response
      })
    },
    [refetchUser, setErrorStatus]
  )

  const logout = () => {
    reachGoal(YandexMetrikaEvent.MAIN_LOGOUT)
    AuthService.logout()
  }

  return {
    user,
    userLoading,
    isSuccess,
    sendSmsCode,
    loginByCode,
    logout
  }
}
